import { useCallback, useEffect, useMemo, useState } from 'react';

import { Trans, t, msg } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Modal, Segment } from 'semantic-ui-react';
import styled from 'styled-components';

import { Checkbox } from 'components/ui/inputs/Checkbox';

import { capitalize } from 'utils/helpers';

import * as svars from 'assets/style/variables';

import { ExportModalFooter } from '../../customer/visualization/scatter-chart/ScatterChartWithExportModal';
import { LightHeader } from '../Header';
import HelpTooltip from '../HelpTooltip';
import ButtonGroup from '../button/ButtonGroup';
import { exportElementAsImage } from '../button/export-as/ExportAsImage';
import { exportAsXls } from '../button/export-as/ExportAsXls';
import { LimitedTextInput } from '../inputs/TextInput';
import ReactTableUi from './ReactTableUi';

const EXPORT_IMAGE_TABLE_ID = 'export-image-table';

const FORMAT_OPTIONS = [
  {
    key: 'xls',
    value: 'xls',
    i18nLabel: msg({ id: 'excel' }),
    icon: 'file excel outline',
  },
  {
    key: 'image',
    value: 'image',
    i18nLabel: msg({ id: 'image' }),
    icon: 'file image outline',
  },
];

function createRowCheckbox({ toggleSelection, findSelected }) {
  function CheckboxComponent({ row }) {
    return (
      <Checkbox
        data-testid="checkbox1"
        checked={findSelected(row?.original?.id)?.selected}
        onChange={() => toggleSelection(row?.original?.id)}
      />
    );
  }

  CheckboxComponent.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      }),
    }),
  };

  CheckboxComponent.defaultProps = {
    row: {},
  };

  return CheckboxComponent;
}

function createHeaderWithCheckbox({ toggleSelection, findSelected, Header }) {
  function CheckboxHeader({ column: { id: columnId, headerLabel } = {} }) {
    return (
      <Checkbox
        data-testid="checkbox2"
        style={{ padding: `${svars.spaceNormalLarge} 0` }}
        checked={findSelected(columnId)}
        onChange={() => toggleSelection(columnId)}
        label={
          <label style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {Header ? <Header /> : capitalize(headerLabel)}
          </label>
        }
      />
    );
  }
  CheckboxHeader.propTypes = {
    column: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      headerLabel: PropTypes.string,
    }),
  };
  CheckboxHeader.defaultProps = {
    column: {},
  };
  return CheckboxHeader;
}

const ImageExportContainer = styled.div`
  width: 400rem;
  height: 400rem;
  position: absolute;
  top: 200%;
  left: 0;
`;

// This component is used to display the table outside of the visible space
// so that it can be freely displayed to be exported as an image without being visible to the user
function TableExportDisplay({
  open,
  onClose,
  columns,
  data,
  exportName,
  onExported,
}) {
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        exportElementAsImage(
          document,
          EXPORT_IMAGE_TABLE_ID,
          exportName,
          onExported
        );
      }, 100);
    }
  }, [open, exportName, onExported]);

  return (
    <ImageExportContainer open={open} onClose={onClose} dimmer={false}>
      <ReactTableUi
        data={data}
        columns={columns}
        sortable={false}
        imageExportId={EXPORT_IMAGE_TABLE_ID}
      />
    </ImageExportContainer>
  );
}

TableExportDisplay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  exportName: PropTypes.string.isRequired,
  onExported: PropTypes.func.isRequired,
};

const makeRowSelectionHeader = (onUnselectAllRows, shouldSelectAllRows) =>
  function RowSelectionHeader() {
    return (
      <HelpTooltip
        compact
        help={[
          shouldSelectAllRows
            ? t({ id: 'select-all-rows' })
            : t({ id: 'unselect-all-rows' }),
        ]}
        trigger={
          <span style={{ display: 'flex' }}>
            <Checkbox
              data-testid="checkbox3"
              onClick={onUnselectAllRows(shouldSelectAllRows)}
              indeterminate={!shouldSelectAllRows}
            />
          </span>
        }
      />
    );
  };

const InlineSegment = styled(Segment)`
  &&& {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

function ExportModal({
  data,
  columns,
  defaultSorted,
  onCloseModal,
  exportName,
  setExportName,
  ...props
}) {
  const [exportFormat, setExportFormat] = useState('xls');
  // Image export modal is a non visible modal used to export the table as an full size image
  const [showImageExportContainer, setShowImageExportContainer] =
    useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(
    columns.map((col) => ({ ...col, selected: true }))
  );
  const [selectedRows, setSelectedRows] = useState(
    data.map((row) => ({ ...row, selected: true }))
  );
  const onToggleAllRows = useCallback(
    (toggleTo) => () => {
      setSelectedRows((prev) =>
        prev.map((row) => ({ ...row, selected: toggleTo }))
      );
    },
    []
  );
  const filteredColumns = useMemo(
    () => selectedColumns.filter((col) => col.selected),
    [selectedColumns]
  );
  const filteredData = useMemo(
    () => selectedRows.filter((row) => row.selected),
    [selectedRows]
  );

  const onExport = useCallback(() => {
    if (exportFormat === 'xls') {
      setExportLoading(true);
      exportAsXls(
        filteredData,
        filteredColumns,
        defaultSorted,
        exportName,
        'Exported Data',
        () => {
          setExportLoading(false);
          onCloseModal();
        }
      );
    } else if (exportFormat === 'image') {
      setShowImageExportContainer(true);
    }
  }, [exportFormat, filteredData, filteredColumns, defaultSorted, exportName]);

  const toggleColumnSelection = useCallback((columnId) => {
    setSelectedColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, selected: !col.selected } : col
      )
    );
  }, []);

  const toggleRowSelection = useCallback((rowId) => {
    setSelectedRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, selected: !row.selected } : row
      )
    );
  }, []);

  const RowCheckbox = useMemo(
    () =>
      createRowCheckbox({
        toggleSelection: toggleRowSelection,
        findSelected: (id) => selectedRows.find((r) => r.id === id),
      }),
    [toggleRowSelection, selectedRows]
  );
  const handleImageExported = useCallback(() => {
    setShowImageExportContainer(false);
    setExportLoading(false);
    onCloseModal();
  }, []);
  const exportTableColumns = useMemo(
    () => [
      {
        id: 'select-row',
        Header: makeRowSelectionHeader(
          onToggleAllRows,
          !selectedRows.some((row) => row.selected)
        ),
        Cell: RowCheckbox,
        minWidth: 30,
        maxWidth: 30,
        width: 30,
      },
      ...columns.map((col) => ({
        ...col,
        // sortable: false,
        Header: createHeaderWithCheckbox({
          toggleSelection: toggleColumnSelection,
          findSelected: (id) =>
            selectedColumns.find((c) => c.id === id)?.selected,
          Header: col.Header,
        }),
      })),
    ],
    [
      RowCheckbox,
      columns,
      toggleColumnSelection,
      selectedColumns,
      onToggleAllRows,
    ]
  );

  const modalIsLoading = exportLoading || showImageExportContainer;

  return (
    <Modal open onClose={onCloseModal} closeIcon>
      <Modal.Header>
        <Trans id="export-a-table" />
      </Modal.Header>
      <Modal.Content
        style={{ maxHeight: '80vh', overflow: 'auto' }}
        data-testid="modal-export-data-content"
      >
        <InlineSegment>
          <LightHeader style={{ marginRight: svars.spaceMedium }}>
            <Trans id="select-an-export-format" /> :
          </LightHeader>
          <ButtonGroup
            data-testid="button-group"
            items={FORMAT_OPTIONS}
            value={exportFormat}
            onChange={(e, { value }) => setExportFormat(value)}
          />
        </InlineSegment>

        <Segment
          style={{
            display: 'flex',
            flexDirection: 'column',
            // Fits the complete modal in a 13-inch screen
            maxHeight: '30rem',
            overflow: 'hidden',
          }}
        >
          <LightHeader style={{ marginBottom: svars.spaceNormalLarge }}>
            <Trans id="unselect-rows-and-columns-before-export" /> :
          </LightHeader>
          <ReactTableUi
            data={data}
            sortable={false}
            columns={exportTableColumns}
            style={{ height: 'auto' }}
            {...props}
          />
        </Segment>
        <InlineSegment>
          <LightHeader style={{ marginRight: svars.spaceMedium }}>
            <Trans id="filename" /> :
          </LightHeader>
          <LimitedTextInput
            style={{
              width: '100%',
              paddingRight: svars.spaceMedium,
              fontWeight: svars.fontWeightSemiBold,
              fontSize: svars.fontSizeBase,
            }}
            value={exportName}
            onChange={(e, { value }) => setExportName(value)}
            maxCharacters={200}
            disabled={modalIsLoading}
          />
        </InlineSegment>
      </Modal.Content>
      <ExportModalFooter
        exportLoading={modalIsLoading}
        onExport={onExport}
        onCloseModal={onCloseModal}
      />
      {showImageExportContainer && (
        <TableExportDisplay
          open={showImageExportContainer}
          onClose={() => setShowImageExportContainer(false)}
          columns={filteredColumns}
          data={filteredData}
          exportName={exportName}
          onExported={handleImageExported}
        />
      )}
    </Modal>
  );
}

ExportModal.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultSorted: PropTypes.arrayOf(PropTypes.shape({})),
  onCloseModal: PropTypes.func.isRequired,
  exportName: PropTypes.string.isRequired,
  setExportName: PropTypes.func.isRequired,
};

ExportModal.defaultProps = {
  defaultSorted: [],
};

function ReactTableWithExportModal({
  data,
  columns,
  modalIsOpen,
  onCloseModal,
  exportName,
  defaultSorted,
  onRowClick,
  ...props
}) {
  return (
    <>
      <ReactTableUi
        data={data}
        columns={columns}
        defaultSorted={defaultSorted}
        onRowClick={onRowClick}
        {...props}
      />
      {modalIsOpen && (
        <ExportModal
          data={data}
          columns={columns}
          defaultSorted={defaultSorted}
          onCloseModal={onCloseModal}
          exportName={exportName}
          {...props}
        />
      )}
    </>
  );
}

ReactTableWithExportModal.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onRowClick: PropTypes.func,
  modalIsOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  exportName: PropTypes.string.isRequired,
  setExportName: PropTypes.func.isRequired,
  defaultSorted: PropTypes.arrayOf(PropTypes.shape({})),
};

ReactTableWithExportModal.defaultProps = {
  defaultSorted: [],
  onRowClick: null,
};

export default ReactTableWithExportModal;
